import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
  notify,
  Poi,
  Route,
  RouteLoader,
  RouteParams,
  RouteRenderer,
  TransportIntervention,
  TransportInterventionPoi,
  TransportInterventionPoiGroupReport
} from 'shared';
import {featureGroup, FeatureGroup, Map, tileLayer} from 'leaflet';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import {TractorService} from '../../stores/tractor.service';
import * as L from 'leaflet';
import {delay, first} from 'rxjs/operators';
import '../../../../../../node_modules/leaflet-timedimension/dist/leaflet.timedimension.src.js';
import {NgElement, WithProperties} from '@angular/elements';
import {MapPoiPopupComponent} from '../../map-poi/map-poi-popup/map-poi-popup.component';
import {MapFieldPopupComponent} from '../../map-fields/map-field-popup/map-field-popup.component';
import {TransportInterventionsMapPoiReportPopupComponent} from '../transport-interventions-map-poi-report-popup/transport-interventions-map-poi-report-popup.component';
import moment from 'moment';
import {Feature, LineString} from 'geojson';

@Component({
  selector: 'app-transport-intervention-map',
  templateUrl: './transport-intervention-map.component.html',
  styleUrls: ['./transport-intervention-map.component.scss']
})
export class TransportInterventionMapComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  intervention: TransportIntervention;

  @Input()
  poiList: TransportInterventionPoi[];

  @Input()
  poiListGroupReport: TransportInterventionPoiGroupReport[];

  @Input()
  hidden: boolean;

  @Input()
  closed: Observable<void>;

  private map: Map;
  private _mapSubject = new ReplaySubject<Map>(1);
  mapReady$ = this._mapSubject.asObservable();

  layers = [
    {
      name: 'Hybride',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
    {
      name: 'Satellite',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
  ];

  mapOptions = {
    layers: this.layers.map(l => l.layer),
    zoom: 7,
    minZoom: 0,
    maxZoom: 21,
    maxNativeZoom: 21,
    preferCanvas: false,
    center: [33.626756199999996, -7.4964748],
    /*timeDimensionControl: true,
    timeDimensionControlOptions: {
      timeSliderDragUpdate: true,
      loopButton: true,
      autoPlay: false,
      position: 'bottomleft',
      playerOptions: {
        transitionTime: 1000,
        loop: true
      }
    },
    timeDimension: false,*/
  };

  poiMapLayer: FeatureGroup = featureGroup();

  route?: Route;
  routeParams: RouteParams;
  routeLoader: RouteLoader = new RouteLoader();
  isRouteLoading: boolean;
  routeLoaded: boolean;
  private closedSub: Subscription;
  private routeLayer: any;
  private routePathLayer: any;
  private timeDimension: any;
  private timeDimensionPlayer: any;
  private timeDimensionControl: any;

  constructor(
    private tractorService: TractorService,
  ) {
  }

  ngOnInit(): void {

    this.closedSub = this.closed.subscribe(_ => {
      this.routeLoaded = false;
      this.poiMapLayer.clearLayers();

      if (this.timeDimensionControl) {
        this.map.removeControl(this.timeDimensionControl);
        this.timeDimensionPlayer.stop();
      }

      if (this.route) {
        this.routeLayer.remove();
        this.routePathLayer.remove();
        // this.routeRenderer.reset();
        this.route = null;
      }
    });
  }

  onMapReady(map: Map) {
    this.map = map;
    this._mapSubject.next(map);
    L.control.scale({imperial: false}).addTo(map);
    map.addLayer(this.poiMapLayer);
  }

  loadRoute() {
    if (this.routeLoaded) {
      return;
    }
    this.mapReady$.pipe(first(), delay(30)).subscribe(() => {
      this.addPoiListToMap();
    });
    this.isRouteLoading = true;
    this.routeParams = new RouteParams(1);
    this.tractorService.route(this.routeLoader,
      this.intervention.vehicle_id,
      this.intervention.start_time,
      this.intervention.stop_time,
      this.routeParams,
    ).subscribe((r) => {
      if (r.isLoaded) {
        this.isRouteLoading = false;
        if (r.isEmpty()) {
          notify('Pas données dans l\'intervale sélectioné');
          return;
        }
        this.route = r.route;

        const icon = L.icon({
          iconUrl: '/assets/icons/circle-marker-green.png',
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        });

        this.routePathLayer = L.geoJSON({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: this.route.data.map(p => p.coords())
          },
          properties: {
            coordTimes: this.route.data.map(p => moment(p.tracker_time).format('YYYY-MM-DD HH:mm:ss'))
          }
        } as Feature<LineString>, {
          pointToLayer: (feature, latLng) => {
            if (feature.properties.hasOwnProperty('last')) {
              return new L.Marker(latLng, {
                icon
              });
            }
            return L.circleMarker(latLng);
          }
        });

        this.newTimeDimension();

        this.routeLayer = L.timeDimension.layer.geoJson(this.routePathLayer, {
          updateTimeDimension: true,
          duration: 'PT2M',
          updateTimeDimensionMode: 'replace',
          addlastPoint: true
        });

        this.routePathLayer.addTo(this.map);
        this.routeLayer.addTo(this.map);
        // this.routeRenderer.render(this.map, this.route, this.routeParams);
      }
      this.routeLoaded = true;
    });
  }

  private addPoiListToMap() {
    this.poiListGroupReport.forEach((r) => {
      const poi = r.poi;
      const layer = poi.layer;

      layer.bindPopup(() => {
        const popupEl = document.createElement('transport-intervention-map-poi-report-popup') as any;
        popupEl.report = r;
        return popupEl;
      }, {
        offset: [0, -20],
        minWidth: 400,
        maxWidth: 400,
        className: 'transport-intervention-map-poi-report-popup',
      });

      layer.bindTooltip(poi.name + r.visits_numbers, {
        direction: poi.type === 'POLYGON' ? 'center' : 'top',
        offset: [0, -40],
        opacity: 0.8,
      });

      this.poiMapLayer.addLayer(layer);
    });
    this.map.fitBounds(this.poiMapLayer.getBounds());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('hidden')) {
      if (!changes.hidden.currentValue && !this.route) {
        this.loadRoute();
      }
    }
  }

  ngOnDestroy(): void {
    this.closedSub.unsubscribe();
  }

  private newTimeDimension() {

    this.timeDimension = new L.timeDimension({
      period: 'PT1S',
    });

    // @ts-ignore
    this.map.timeDimension = this.timeDimension;

    // @ts-ignore
    this.timeDimensionPlayer = new L.TimeDimension.Player({
      transitionTime: 1000,
      loop: true
    }, this.timeDimension);

    // @ts-ignore
    this.timeDimensionControl = new L.Control.TimeDimension({
      player: this.timeDimensionPlayer,
      timeDimension: this.timeDimension,
      timeSliderDragUpdate: true,
      loopButton: true,
      autoPlay: false,
      position: 'bottomleft',
    });
    this.map.addControl(this.timeDimensionControl);
  }
}
